import { useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import { usePocketBase } from "../hooks/usePocketBase";

type LoginFormState = {
  email: string;
  password: string;
};

const initialState: LoginFormState = {
  email: "",
  password: "",
};

export function LoginPage() {
  const pb = usePocketBase();
  const [form, setForm] = useState<LoginFormState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  function handleChange<K extends keyof LoginFormState>(
    field: K,
    value: LoginFormState[K],
  ) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      const authResult = await pb
        .collection("users")
        .authWithPassword(form.email, form.password);

      const displayName =
        authResult.record.name?.trim() || authResult.record.email || "User";
      setSuccessMessage(`Logged in as ${displayName}.`);
      setForm(initialState);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Login failed.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography component="h1" variant="h4" sx={{ mb: 1 }}>
          Log in
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 0 }}>
          Sign in to your account.
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            required
            autoComplete="email"
            label="Email"
            margin="normal"
            type="email"
            value={form.email}
            onChange={(event) => handleChange("email", event.target.value)}
          />

          <TextField
            fullWidth
            required
            autoComplete="current-password"
            label="Password"
            margin="normal"
            type="password"
            value={form.password}
            onChange={(event) => handleChange("password", event.target.value)}
          />

          <Button
            fullWidth
            disabled={isSubmitting}
            sx={{ mt: 3 }}
            type="submit"
            variant="contained"
          >
            {isSubmitting ? "Logging in..." : "Log in"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default LoginPage;
